import {
  BookOpen,
  Building2,
  Calendar,
  CircleDollarSign,
  ContactRound,
  FolderKanban,
  LayoutDashboard,
  Network,
  PanelsTopLeft,
  Sparkles,
  ShieldCheck,
  UsersRound,
  GraduationCap,
} from 'lucide-react';
import type { OrganizationMembershipResponse } from '../api';

export type NavItem = {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  activeMatchers?: string[];
  children?: Array<{
    id: string;
    title: string;
    icon?: React.ComponentType<{ className?: string }>;
    href: string;
    activeMatchers?: string[];
  }>;
};

export interface AppSidebarProps {
  userOrganizations?: OrganizationMembershipResponse | null;
  className?: string;
  onLogout?: () => void;
}

export type SidebarPermissions = {
  isSuperAdmin: boolean;
  isPublicTenant: boolean;
  isStudent: boolean;
  isTeacher: boolean;
  isSchoolAdmin: boolean;
  hasLMSModule: boolean;
  hasProjectModule: boolean;
  isCompany: boolean;
};

export const buildSidebarNavigation = ({
  isSuperAdmin,
  isPublicTenant,
  isStudent,
  isTeacher,
  isSchoolAdmin,
  hasLMSModule,
  hasProjectModule,
  isCompany,
}: SidebarPermissions): NavItem[] => {
  
  if (isPublicTenant && isSuperAdmin) {
    return [
      {
        id: 'public-dashboard',
        title: 'Dashboard',
        icon: LayoutDashboard,
        href: '/dashboard',
        activeMatchers: ['/dashboard'],
      },
      {
        id: 'public-skills',
        title: 'Skills',
        icon: Sparkles,
        href: '/skills',
        activeMatchers: ['/skills'],
      },
      {
        id: 'public-plans',
        title: 'Plans',
        icon: CircleDollarSign,
        href: '/plans',
        activeMatchers: ['/plans'],
      },
      {
        id: 'public-clients',
        title: 'Clients',
        icon: ContactRound,
        href: '/clients',
        activeMatchers: ['/clients'],
      },
    ];
  }

  const common: NavItem[] = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: LayoutDashboard,
      href: '/dashboard',
      activeMatchers: ['/dashboard'],
    },
    ...((!isCompany || isSchoolAdmin)
  ? [
      {
        id: 'careers',
        title: 'Careers',
        icon: GraduationCap,
        href: isStudent ? '/career-overview' : '/jobs',
        activeMatchers: isStudent
          ? ['/career-overview']
          : ['/jobs'],
      } satisfies NavItem,
    ]
  : []),
    ...(hasLMSModule
      ? [
        {
          id: 'courses',
          title: 'Courses',
          icon: BookOpen,
          href: '/courses',
          activeMatchers: ['/courses'],
        } satisfies NavItem,
      ]
      : []),
    ...(hasProjectModule
      ? [
        {
          id: 'projects',
          title: 'Projects',
          icon: FolderKanban,
          href: '/projects',
          activeMatchers: ['/projects'],
        } satisfies NavItem,
      ]
      : []),
    {
      id: 'calendar',
      title: 'Calendar',
      icon: Calendar,
      href: '/calendar',
      activeMatchers: ['/calendar'],
    },
  ];

  if (isSchoolAdmin) {
    return [
      ...common,
      {
        id: 'admin-tools',
        title: 'Admin tools',
        icon: ShieldCheck,
        href: '#',
        activeMatchers: [
          '/admin-tools/skills-and-competences',
          '/admin-tools/active-plan',
          '/admin-tools/manage-organization',
          '/admin-tools/boards',
          '/admin-tools/manage-users',
        ],
        children: [
          {
            id: 'manage-framework',
            title: 'Manage frameworks',
            icon: Network,
            href: '/admin-tools/skills-and-competences',
            activeMatchers: ['/admin-tools/skills-and-competences'],
          },
          {
            id: 'active-plan',
            title: 'Active plan',
            icon: CircleDollarSign,
            href: '/admin-tools/active-plan',
            activeMatchers: ['/admin-tools/active-plan'],
          },
          {
            id: 'manage-organization',
            title: 'Manage organization profile',
            icon: Building2,
            href: '/admin-tools/manage-organization',
            activeMatchers: ['/admin-tools/manage-organization'],
          },
          {
            id: 'manage-boards',
            title: 'Manage boards',
            icon: PanelsTopLeft,
            href: '/admin-tools/boards',
            activeMatchers: ['/admin-tools/boards'],
          },
          {
            id: 'manage-users',
            title: 'Manage users',
            icon: UsersRound,
            href: '/admin-tools/manage-users',
            activeMatchers: ['/admin-tools/manage-users'],
          },
        ],
      },
    ];
  }

  if (isTeacher) {
    return [
      ...common,
      {
        id: 'manage-framework',
        title: 'Manage frameworks',
        icon: Network,
        href: '/admin-tools/skills-and-competences',
        activeMatchers: ['/admin-tools/skills-and-competences'],
      },
      {
        id: 'manage-boards',
        title: 'Manage boards',
        icon: PanelsTopLeft,
        href: '/admin-tools/boards',
        activeMatchers: ['/admin-tools/boards'],
      },
    ];
  }

  if (isStudent) {
    return [
      ...common,
      {
        id: 'my-boards',
        title: 'My boards',
        icon: PanelsTopLeft,
        href: '/my-boards',
        activeMatchers: ['/my-boards'],
      },
      {
        id: 'frameworks',
        title: 'Frameworks',
        icon: Network,
        href: '/admin-tools/skills-and-competences',
        activeMatchers: ['/admin-tools/skills-and-competences'],
      },
    ];
  }

  return common;
};
